import { ArrowSmRightIcon, RefreshIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Form,
  ListItem,
  ErrorMissingData,
  ErrorNotLoggedIn,
  Paragraph,
  StickyFooter,
  Title,
} from 'src/components'
import { savedTokens } from 'src/auth'
import { clearStorageCache } from 'src/storage'
import { getEnvString } from 'src/utils'
import { useEditOrganization } from '../Onboarding/queries'
import { getAdminPolicy, getConnectPolicy } from './policy'
import {
  useCreateIAMUser,
  useCreateIAMPolicy,
  useAttachIAMPolicy,
  useCreateFullUserPool,
  useResyncSubscriptionProvisioning,
} from './queries'
import {
  savedAccessKey,
  savedAccessSecret,
  savedAccountId,
  savedInstanceId,
  savedRegion,
  savedSetupAccessKey,
  savedSetupAccessSecret,
  savedUserGroup,
  savedConnectUrl,
  savedArn,
} from './storage'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import CustomButton from 'src/components/Shared/UIComponents/Button'

type Props = {
  organization: string
  username: string
  apiAccessKey: string
  apiAccessSecret: string
  region: string
  userGroup: string
  accountId: string
  instanceId: string
  connectUrl: string
  arn: string
}

function CreateItems(props: Props) {
  const navigate = useNavigate()
  const createAdminUser = useCreateIAMUser()
  const createAdminPolicy = useCreateIAMPolicy()
  const createConnectPolicy = useCreateIAMPolicy()
  const attachAdminPolicy = useAttachIAMPolicy()
  const editOrganization = useEditOrganization()
  const createUserPool = useCreateFullUserPool()
  const resyncSubscriptions = useResyncSubscriptionProvisioning()

  return (
    <Form
      onSubmit={async (e) => {
        e.preventDefault()

        const userData = await createAdminUser.mutateAsync({
          organization: props.organization,
          username: props.username,
          api_version: getEnvString('REACT_APP_API_VERSION'),
          api_accessKey: props.apiAccessKey,
          api_accessSecret: props.apiAccessSecret,
          api_region: props.region,
          usergroup: props.userGroup,
          usernameforcreate: `mc_${props.organization}_admin`,
        })

        savedAccessKey.set(userData.AccessData.AccessKeyId)
        savedAccessSecret.set(userData.AccessData.SecretAccessKey)

        const [adminPolicy, connectPolicy] = await Promise.all([
          createAdminPolicy.mutateAsync({
            organization: props.organization,
            username: props.username,
            api_version: getEnvString('REACT_APP_API_VERSION'),
            api_accessKey: props.apiAccessKey,
            api_accessSecret: props.apiAccessSecret,
            api_region: props.region,
            policyname: `mc_admin_${props.organization}_policy`,
            policy: getAdminPolicy(
              props.region,
              props.accountId,
              props.instanceId,
              props.userGroup,
            ),
            usergroup: props.userGroup,
          }),
          createConnectPolicy.mutateAsync({
            organization: props.organization,
            username: props.username,
            api_version: getEnvString('REACT_APP_API_VERSION'),
            api_accessKey: props.apiAccessKey,
            api_accessSecret: props.apiAccessSecret,
            api_region: props.region,
            policyname: `mc_connect_${props.organization}_policy`,
            policy: getConnectPolicy(
              props.region,
              props.accountId,
              props.instanceId,
            ),
            usergroup: props.userGroup,
          }),
        ])

        await attachAdminPolicy.mutateAsync({
          organization: props.organization,
          username: props.username,
          api_version: getEnvString('REACT_APP_API_VERSION'),
          api_accessKey: props.apiAccessKey,
          api_accessSecret: props.apiAccessSecret,
          api_region: props.region,
          policyarn: adminPolicy.Arn,
          usernameforpolicy: `mc_${props.organization}_admin`,
        })

        const userPool = await createUserPool.mutateAsync({
          organization: props.organization,
          username: props.username,
          poolname: props.organization,
          domain: `mobileconnect-${props.organization}`,
        })

        await editOrganization.mutateAsync({
          cognito_url: userPool.cognitourl,
          userPoolAppClientId: userPool.clientid,
          userPoolClientSecret: userPool.userpoolclientsecret,
          userPoolId: userPool.userpoolid,
          userPoolRegion: userPool.userpoolregion,
          aws_connect_type: 'SAML',
          aws_connect_saml_url: props.connectUrl,
          iam_organization_admin_accessKey: userData.AccessData.AccessKeyId,
          iam_organization_admin_accessSecret:
            userData.AccessData.SecretAccessKey,
          iam_organization_admin_region: props.region,
          iam_organization_policy_arn: connectPolicy.Arn,
          organization_connect_instance_id: props.instanceId,
          organization_connect_account_id: props.accountId,
          organization_connect_arn: props.arn,
          code: props.organization,
          setupStep: 'finished',
          username: props.username,
          organization: props.organization,
          region: props.region,
          application: {
            chatregion: props.region,
          },
        })

        await resyncSubscriptions.mutateAsync({
          organization: props.organization,
        })

        navigate('../validate')
        clearStorageCache('SETUP')
      }}
    >
      <Title label="Create Items" className="col-span-full" />

      <Paragraph className="col-span-full">
        We've successfully set up the setup user, with that user we can create
        all we need.
      </Paragraph>

      <ul className="col-span-full">
        <ListItem label="Create Admin User" status={createAdminUser.status} />

        <ListItem
          label="Create Admin Policy"
          status={createAdminPolicy.status}
        />

        <ListItem
          label="Attach Admin Policy"
          status={attachAdminPolicy.status}
        />

        <ListItem
          label="Create Connect Policy"
          status={createConnectPolicy.status}
        />

        <ListItem
          label="Configure Organization"
          status={editOrganization.status}
        />

        <ListItem
          label="Sync entitlements"
          status={resyncSubscriptions.status}
        />
      </ul>

      <StickyFooter>
        <EmailHelpBox className="flex-1" />
        <CustomButton
          type="submit"
          label="Create"
          loadingLabel="Creating..."
          icon={<ArrowSmRightIcon />}
          iconPosition="right"
          isLoading={
            createAdminUser.isLoading ||
            createAdminPolicy.isLoading ||
            attachAdminPolicy.isLoading ||
            createConnectPolicy.isLoading ||
            editOrganization.isLoading ||
            resyncSubscriptions.isLoading
          }
        />
      </StickyFooter>
    </Form>
  )
}

export default function ValidateCreateItems() {
  const navigate = useNavigate()
  const [tokens] = savedTokens.useState(null)
  const [setupAccessKey] = savedSetupAccessKey.useState('')
  const [setupAccessSecret] = savedSetupAccessSecret.useState('')
  const [region] = savedRegion.useState('')
  const [userGroup] = savedUserGroup.useState('')
  const [accountId] = savedAccountId.useState('')
  const [instanceId] = savedInstanceId.useState('')
  const [arn] = savedArn.useState('')
  const [connectUrl] = savedConnectUrl.useState('')

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  if (
    !setupAccessKey ||
    !setupAccessSecret ||
    !region ||
    !userGroup ||
    !accountId ||
    !instanceId ||
    !connectUrl ||
    !arn
  ) {
    return (
      <ErrorMissingData
        missingValues={{
          setupAccessKey: !setupAccessKey,
          setupAccessSecret: !setupAccessSecret,
          region: !region,
          userGroup: !userGroup,
          accountId: !accountId,
          instanceId: !instanceId,
          arn: !arn,
          connectUrl: !connectUrl,
        }}
      >
        <Button
          label="restart setup"
          onClick={() => navigate('../arn')}
          className="ml-8"
          icon={<RefreshIcon />}
          iconPosition="left"
        />
      </ErrorMissingData>
    )
  }

  return (
    <CreateItems
      organization={tokens.organization}
      username={tokens.username}
      apiAccessKey={setupAccessKey}
      apiAccessSecret={setupAccessSecret}
      region={region}
      userGroup={userGroup}
      accountId={accountId}
      instanceId={instanceId}
      connectUrl={connectUrl}
      arn={arn}
    />
  )
}
